import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, ScrollText, Wallet, FileText, ClipboardList, Star, Building2, TrendingUp, Eye, Plus,
} from "lucide-react";
import api from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { agencyMenu } from "@/config/menus";
import { KpiCard, PageHeader } from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusPill, VerifiedBadge, inr, LoadingScreen } from "@/components/common";
import { useAuth } from "@/context/AuthContext";

const ALL_SERVICES = ["Unarmed Security Guard", "Armed Security Guard", "Female Security Guard", "Security Supervisor", "Bouncer", "Event Security", "Factory Security", "Warehouse Security", "Construction Site Security", "Office Security", "Hotel Security", "Hospital Security", "School Security", "Society Security", "CCTV Installation", "CCTV Monitoring", "Access Control", "Biometric Attendance", "VIP Protection", "Night Patrol"];

function Onboard({ onDone }) {
  const { checkAuth } = useAuth();
  const [cities, setCities] = useState([]);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ name: "", description: "", established_year: 2018, head_office: { city: "", state: "", pincode: "" }, service_locations: "", service_pincodes: "", psara_number: "", gst_number: "", pan: "", years_experience: 5, price_per_guard_month: 15000, services: [] });
  useEffect(() => { api.get("/cities").then((r) => setCities(r.data)).catch(() => {}); }, []);
  const toggle = (s) => setF((p) => ({ ...p, services: p.services.includes(s) ? p.services.filter((x) => x !== s) : [...p.services, s] }));

  const submit = async () => {
    if (!f.name || !f.head_office.city) return toast.error("Agency name and city are required");
    setSaving(true);
    try {
      await api.post("/agencies", {
        ...f, established_year: Number(f.established_year), years_experience: Number(f.years_experience),
        price_per_guard_month: Number(f.price_per_guard_month),
        service_locations: f.service_locations.split(",").map((s) => s.trim()).filter(Boolean),
        service_pincodes: f.service_pincodes.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Agency profile created! Submit documents for verification.");
      await checkAuth();
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl">
      <div className="bg-navy-900 text-white rounded-xl p-6 mb-6">
        <Building2 className="text-brand" size={28} />
        <h2 className="font-heading text-2xl font-bold mt-3">Register your security agency</h2>
        <p className="text-navy-300 mt-1 text-sm">Create your company profile to start receiving leads. Your Verified badge is granted by our team after document review.</p>
      </div>
      <div className="bg-white border border-navy-200 rounded-xl p-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-xs">Agency Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="ag-name" /></div>
          <div><Label className="text-xs">Established Year</Label><Input type="number" value={f.established_year} onChange={(e) => setF({ ...f, established_year: e.target.value })} data-testid="ag-year" /></div>
          <div><Label className="text-xs">Head Office City *</Label>
            <Select value={f.head_office.city} onValueChange={(v) => { const c = cities.find((x) => x.city === v); setF({ ...f, head_office: { city: v, state: c?.state || "", pincode: c?.pincode || "" } }); }}>
              <SelectTrigger data-testid="ag-city"><SelectValue placeholder="Select city" /></SelectTrigger>
              <SelectContent>{cities.map((c) => <SelectItem key={c.city} value={c.city}>{c.city}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Years of Experience</Label><Input type="number" value={f.years_experience} onChange={(e) => setF({ ...f, years_experience: e.target.value })} data-testid="ag-exp" /></div>
          <div><Label className="text-xs">PSARA Number</Label><Input value={f.psara_number} onChange={(e) => setF({ ...f, psara_number: e.target.value })} data-testid="ag-psara" /></div>
          <div><Label className="text-xs">GST Number</Label><Input value={f.gst_number} onChange={(e) => setF({ ...f, gst_number: e.target.value })} data-testid="ag-gst" /></div>
          <div><Label className="text-xs">Service Locations (comma separated)</Label><Input value={f.service_locations} onChange={(e) => setF({ ...f, service_locations: e.target.value })} placeholder="Ahmedabad, Gandhinagar" data-testid="ag-locations" /></div>
          <div><Label className="text-xs">Price per guard/month (₹)</Label><Input type="number" value={f.price_per_guard_month} onChange={(e) => setF({ ...f, price_per_guard_month: e.target.value })} data-testid="ag-price" /></div>
        </div>
        <div><Label className="text-xs">Description</Label><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="ag-desc" /></div>
        <div>
          <Label className="text-xs">Services Offered</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {ALL_SERVICES.map((s) => (
              <button key={s} onClick={() => toggle(s)} data-testid={`ag-svc-${s.replace(/\s/g, "-").toLowerCase()}`}
                className={`text-xs px-3 py-1.5 rounded-full border ${f.services.includes(s) ? "bg-navy-900 text-white border-navy-900" : "border-navy-200 text-navy-600"}`}>{s}</button>
            ))}
          </div>
        </div>
        <Button className="bg-brand hover:bg-brand-600 w-full h-11" disabled={saving} onClick={submit} data-testid="ag-submit">{saving ? "Creating…" : "Create Agency Profile"}</Button>
      </div>
    </div>
  );
}

export default function AgencyDashboard() {
  const navigate = useNavigate();
  const [d, setD] = useState(undefined); // undefined=loading, null=no agency
  const load = () => api.get("/dashboard/agency").then((r) => setD(r.data)).catch((e) => { if (e.response?.status === 404 || e.response?.status === 403) setD(null); else setD(null); });
  useEffect(() => { load(); }, []);

  if (d === undefined) return <DashboardShell menu={agencyMenu} title="Agency Portal"><LoadingScreen /></DashboardShell>;
  if (d === null) return <DashboardShell menu={[agencyMenu[0]]} title="Agency Registration"><Onboard onDone={load} /></DashboardShell>;

  const a = d.agency;
  return (
    <DashboardShell menu={agencyMenu} title="Agency Dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-navy-900 text-white flex items-center justify-center font-heading font-bold text-lg">{a.name[0]}</div>
          <div><h2 className="font-heading text-xl font-bold text-navy-900">{a.name}</h2><div className="flex items-center gap-2 mt-1"><VerifiedBadge verified={a.verified} /><StatusPill status={a.verification_status} /><span className="text-xs text-navy-500">{a.subscription_plan} plan</span></div></div>
        </div>
        <Button variant="outline" className="border-navy-300" onClick={() => navigate("/agency/profile")}>Manage Profile</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard icon={ClipboardList} label="New Leads" value={d.new_leads} accent testid="kpi-leads" />
        <KpiCard icon={FileText} label="Pending Quotes" value={d.pending_quotations} testid="kpi-quotes" />
        <KpiCard icon={ScrollText} label="Active Contracts" value={d.active_contracts} testid="kpi-contracts" />
        <KpiCard icon={Users} label="Active Customers" value={d.active_customers} testid="kpi-customers" />
        <KpiCard icon={Users} label="Total Guards" value={d.total_guards} testid="kpi-guards" />
        <KpiCard icon={Wallet} label="Monthly Revenue" value={inr(d.monthly_revenue)} testid="kpi-mrr" />
        <KpiCard icon={TrendingUp} label="Total Revenue" value={inr(d.total_revenue)} testid="kpi-revenue" />
        <KpiCard icon={Star} label="Rating" value={`${d.rating || 0}/5`} testid="kpi-rating" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[["New Quotation", ClipboardList, "/agency/leads", true], ["Add Guard", Plus, "/agency/guards", false], ["Manage Staff", Users, "/agency/guards", false], ["View Contracts", ScrollText, "/agency/contracts", false]].map(([l, Icon, to, accent]) => (
          <button key={l} onClick={() => navigate(to)} data-testid={`quick-${l.replace(/\s/g, "-").toLowerCase()}`}
            className={`card-lift rounded-xl p-5 text-left border ${accent ? "bg-brand text-white border-brand" : "bg-white border-navy-200 text-navy-900"}`}>
            <Icon size={22} className={accent ? "text-white" : "text-brand"} /><div className="mt-3 font-heading font-semibold">{l}</div>
          </button>
        ))}
      </div>

      <h3 className="font-heading text-lg font-bold text-navy-900 mb-4">Recent Contracts</h3>
      {d.contracts.length === 0 ? <p className="text-navy-500 text-sm">No contracts yet. Respond to leads to win contracts.</p> : (
        <div className="bg-white border border-navy-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead className="bg-navy-50 text-navy-500 text-xs uppercase"><tr><th className="text-left p-3">Customer</th><th className="text-left p-3">Location</th><th className="p-3">Guards</th><th className="p-3">Monthly</th><th className="p-3">Status</th></tr></thead>
            <tbody>{d.contracts.map((c) => (
              <tr key={c.id} className="border-t border-navy-100"><td className="p-3 font-medium text-navy-900">{c.customer_name}</td><td className="p-3 text-navy-600">{c.service_location}</td><td className="p-3 text-center">{c.num_guards}</td><td className="p-3 text-center font-semibold">{inr(c.monthly_charges)}</td><td className="p-3 text-center"><StatusPill status={c.status} /></td></tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </DashboardShell>
  );
}
